import { Injectable } from '@angular/core';
import {Observable, of, OperatorFunction, pipe} from 'rxjs';
import { AbstractSearchResultService } from './abstract-search-result.service';
import { AggregateOptions } from '../../../models/aggregate-request-params';
import { EsPage } from '../../../models/es-page';
import { CdxTask } from '../../../models/cdx-task';
import { InternalRoutes } from '../../../models/internal-routes';
import { ObjectType } from '../../../models/ObjectType';
import { SearchItem } from '../../../models/search-item';
import {
    selectWkfSupervisionSearchItemsDatas
} from '../../wkf-supervision-search/wkf-supervision-search-item/wkf-supervision-search-item-selectors';
import {
    selectWkfSupervisionFilterDatas, selectWkfSupervisionFilterFeature
} from '../../wkf-supervision-search/wkf-supervision-filter/wkf-supervision-filter-selectors';
import {
    selectWkfSupervisionSearchResultDatas, selectWkfSupervisionSearchResultFeature
} from '../../wkf-supervision-search/wkf-supervision-search-result/wkf-supervision-search-result-selectors';
import {
    WkfSupervisionSearchResultState
} from '../../wkf-supervision-search/wkf-supervision-search-result/reducer/wkf-supervision-search-result.reducer';
import {
    ProcessStateEnum,
    WkfSupervisionFilterDatas,
    WkfSupervisionFilterState
} from '../../wkf-supervision-search/wkf-supervision-filter/reducer/wkf-supervision-filter.reducer';
import { AbstractFilterService } from '../../filter/service/abstract-filter.service';
import { Page } from '../../../models/page';
import { HttpClient, HttpErrorResponse, HttpParams } from '@angular/common/http';
import { WkfCfgFieldService } from '../../field/service/wkf/wkf-cfg-field.service';
import { ThesaurusService } from '../../../services/thesaurus/thesaurus.service';
import { TaskFilterService } from '../../filter/service/task-filter.service';
import { SearchResultPaginationService } from '../../search-result-pagination/service/search-result-pagination.service';
import {
    WkfSupervisionSearchResultAction
} from '../../wkf-supervision-search/wkf-supervision-search-result/action/wkf-supervision-search-result-action';
import { ConfigurationService } from '../../../../entry-routes/configuration/configuration.service';
import { Store } from '@ngrx/store';
import { CurrentSupervisionService } from '../../current-supervision/service/current-supervision.service';
import { AuthenticationService } from '../../../../entry-routes/authentication/authentication.service';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { Url } from '../../../models/url';
import { SearchPath } from '../../../models/search-path';
import { Aggregate } from '../../../models/aggregate';
import {map, switchMap, withLatestFrom} from 'rxjs/operators';
import { TaskSearchResultService } from './task-search-result.service';



@Injectable( {
    providedIn: 'root'
} )
export class WkfSupervisionSearchResultService extends AbstractSearchResultService {
    public static readonly WKFSUPERVISION_PAGE_DEFAULT_SORT_KEY = 'cdx_wkf.cdx_process.process_instance_id';
    public static readonly WKFSUPERVISION_PAGE_DEFAULT_SORT_DIRECTION = 'desc';

    wkfSupervisionSearchItems$ = this.store.select(selectWkfSupervisionSearchItemsDatas);
    wkfSupervisionFilterDatas$ = this.store.select(selectWkfSupervisionFilterDatas);
    wkfSupervisionSearchListResult$ = this.store.select(selectWkfSupervisionSearchResultDatas);

    private wkfSupervisionSearchResultState: WkfSupervisionSearchResultState;
    private wkfSupervisionFilterState: WkfSupervisionFilterState;

    private filterService: AbstractFilterService;

    static defaultPage  =new Page<CdxTask>([], 0, 0, 10, 0, false, true, {
        pageNumber: 0,
        pageSize: 10,
        sort: { active: WkfSupervisionSearchResultService.WKFSUPERVISION_PAGE_DEFAULT_SORT_KEY , direction: WkfSupervisionSearchResultService.WKFSUPERVISION_PAGE_DEFAULT_SORT_DIRECTION}
    } );

    public forceRefresh : boolean;

    constructor(
        http: HttpClient,
        fieldService: WkfCfgFieldService,
        thesaurusService: ThesaurusService,
        taskFilterService: TaskFilterService,
        protected searchResultPaginationService: SearchResultPaginationService,
        private wkfSupervisionSearchResultAction: WkfSupervisionSearchResultAction,
        private configAction: ConfigurationService,
        store: Store,
        private currentSupervisionService: CurrentSupervisionService,
        authenticationService: AuthenticationService
    ) {
        super(
            http,
            fieldService,
            thesaurusService,
            store,
            authenticationService
        );

        this.filterService = taskFilterService;

        this.store.select(selectWkfSupervisionSearchResultFeature)
            .pipe(takeUntilDestroyed())
            .subscribe(wkfSupervisionSearchResultState => this.wkfSupervisionSearchResultState = wkfSupervisionSearchResultState);

        this.store.select(selectWkfSupervisionFilterFeature)
            .pipe(takeUntilDestroyed())
            .subscribe(wkfSupervisionFilterState => this.wkfSupervisionFilterState = wkfSupervisionFilterState);
    }

    public getForceRefresh(){
        return this.forceRefresh;
    }

    public setForceRefresh(forceRefresh : boolean){
        this.forceRefresh=forceRefresh;
    }

    public getServiceType(): InternalRoutes {
        return InternalRoutes.TASKS;
    }

    public getObjectType(): ObjectType {
        return ObjectType.TASK;
    }

    public getSearchItem$(): Observable<SearchItem[]> {
        return this.wkfSupervisionSearchItems$;
    }

    protected getFilterDatas$(): Observable<WkfSupervisionFilterDatas> {
        return this.wkfSupervisionFilterDatas$;
    }

    protected getSearchListResult$(): Observable<EsPage<CdxTask>> {
        return this.wkfSupervisionSearchListResult$;
    }

    protected getSearchBaseUrl(): string {
        return Url.getWkfProtectedApiBaseUrl(this.configAction) + Url.SUPERVISION + Url.SEARCH;
    }

    protected getAggBaseUrl(): string {
        return Url.getWkfProtectedApiBaseUrl(this.configAction) + Url.TASKS + Url.AGGS;
    }

    protected getExportExcelBaseUrl(): string {
        return Url.getWkfProtectedApiBaseUrl(this.configAction) + Url.SUPERVISION + Url.EXCEL;
    }

    protected getEmptyEspage(): EsPage<CdxTask> {
        return new EsPage<CdxTask>(0, [], 0, 0, [], false);
    }

    public searchDrafts(pageNumber: any, keepLastAgg: any): void {
        console.log('wkf-supervision-search-result-servcice searchDrafts() (not implemented) pageNumber:'+pageNumber+' keepLastAgg:'+keepLastAgg)
        return;
    }

    public search(keepFilter=false): void {

        if(!keepFilter) {
            if ( !this.wkfSupervisionFilterState || !this.wkfSupervisionFilterState.datas || !this.wkfSupervisionFilterState.datas.lastAgg ) {
                this.filterService.removeAllFilters();
            } else {
                const searchPath: SearchPath = new SearchPath( this.wkfSupervisionFilterState.datas.lastAgg.path );
                if ( searchPath.code !== SearchPath.META_CDX_TYPE ) {
                    this.filterService.removeAllFilters();
                } else {
                    this.filterService.removeFiltersAndKeepLastAgg();
                }
            }
        }

        this.wkfSupervisionSearchResultAction.loadTasksStarted();
        try {
            super._search().subscribe({
                next: (page: EsPage<CdxTask>) => {
                    console.debug("wkfSupervisionSearchResultService search() called  and super._search() result begin ")

                    // load unknown fields
                    this.cfgFieldService.loadFieldsFromPath(...page.aggs.filter((agg: Aggregate) => agg.path !== SearchPath.META_CDX_TYPE).map((agg: Aggregate) => {
                        let path = '';
                        if(!!agg.path) {
                            path = agg.path;
                        } else {
                            if(agg?.type == 'DELAY') {
                                path = agg.delayPath;
                            }
                        }
                        return path;
                    }));
                    if (!!this.wkfSupervisionFilterState && !!this.wkfSupervisionFilterState.datas && !!this.wkfSupervisionFilterState.datas.lastAgg) {
                        const lastAggSearchPath: SearchPath = new SearchPath(this.wkfSupervisionFilterState.datas.lastAgg.path);
                        if (lastAggSearchPath.isMeta && lastAggSearchPath.code === SearchPath.META_CDX_TYPE) {
                            page.aggs = page.aggs.map((aggregate: Aggregate) => {
                                if (aggregate.path === this.wkfSupervisionFilterState.datas.lastAgg.path) {
                                    return this.wkfSupervisionFilterState.datas.lastAgg;
                                }
                                return aggregate;
                            });
                        }
                        if(!keepFilter) {
                            this.filterService.removeAllFilters();
                        }
                    }

                    // send result to store
                    this.wkfSupervisionSearchResultAction.loadTasksSucceeded(page as EsPage<CdxTask>);

                    console.debug("wkfSupervisionSearchResultService search() called  and super._search() result end (after loadTasksSucceeded)")
                },
                error: (error) => {
                    this.wkfSupervisionSearchResultAction.loadTasksFailed(error);
                    // si plantage cela peut être du au tri, on le retire du store pour pouvoir relancer la recherche sans
                    this.searchResultPaginationService.reinitSearchResultPagination();
                }
            });
        }
        catch (error) {
            this.wkfSupervisionSearchResultAction.loadTasksFailed(error);
        }
    }

    public setNewPageToStore(page: EsPage<CdxTask>) {
        this.filterService.removeAllFilters();

        this.cfgFieldService.loadFieldsFromPath(...page.aggs.filter((agg: Aggregate) => agg.path !== SearchPath.META_CDX_TYPE).map((agg: Aggregate) => agg.path));
        // send result to store
        this.wkfSupervisionSearchResultAction.loadTasksSucceeded(page as EsPage<CdxTask>);
    }

    public filter(keepLastAgg = false): void {
        this.wkfSupervisionSearchResultAction.loadTasksStarted();

        try {
            super._filter()
                .subscribe(
                    {
                        next: (page: EsPage<CdxTask>) => {
                            // load unknown fields
                            // this.fieldService.loadFieldsFromPath(...page.aggs.filter((agg: Aggregate) => agg.path !== SearchPath.META_CDX_TYPE).map((agg: Aggregate) => agg.path));
                            if (!!this.wkfSupervisionFilterState && !!this.wkfSupervisionFilterState.datas && !!this.wkfSupervisionFilterState.datas.lastAgg && keepLastAgg) {
                                page.aggs = page.aggs.map((aggregate: Aggregate) => {
                                    if ((   aggregate.path           === this.wkfSupervisionFilterState.datas.lastAgg.path
                                        || aggregate.path + '.code' === this.wkfSupervisionFilterState.datas.lastAgg.path)
                                        && aggregate.type === this.wkfSupervisionFilterState.datas.lastAgg.type
                                    ) {
                                        // cas particulier task_def et process_def : le .code est ajouté lors de l'appel au filtre,
                                        // donc on teste aussi -> "cdx_wkf.cdx_process.task_def.code" et "cdx_wkf.cdx_process.process_def.code"
                                        // pour récupérer / conserver les autres buckets apres filtre
                                        return this.wkfSupervisionFilterState.datas.lastAgg;
                                    }

                                    return aggregate;
                                });
                            }
                            // send result to store
                            this.wkfSupervisionSearchResultAction.loadTasksSucceeded(page as EsPage<CdxTask>);
                        },
                        error: (error: HttpErrorResponse) => {
                            this.wkfSupervisionSearchResultAction.loadTasksFailed(error);
                        }
                    }
                );
        } catch (error) {
            this.wkfSupervisionSearchResultAction.loadTasksFailed(error);
        }
    }

    public loadAggregate(path: string, opts: AggregateOptions, checkPath?: boolean): void {
        this.wkfSupervisionSearchResultAction.loadAggregateStart();
        try {
            this._loadAggregate(path, opts, checkPath)
                .subscribe(
                        {
                            next: (agg: Aggregate) => {
                                // this.fieldService.loadFieldsFromPath(path);
                                this.wkfSupervisionSearchResultAction.loadAggregateSucceeded(agg);
                            },
                            error: (error: HttpErrorResponse) => {
                                this.wkfSupervisionSearchResultAction.loadAggregateFailed(error);
                            }
                        }
                    );
        } catch (error) {
            this.wkfSupervisionSearchResultAction.loadAggregateFailed(error);
        }
    }

    public gotoPage(pageNum: number): void {
        this.wkfSupervisionSearchResultAction.updateTasksStarted();
        try {
            super._gotoPage(pageNum)
                .subscribe((page: EsPage<CdxTask>) => {
                        // load unknown fields
                        this.cfgFieldService.loadFieldsFromPath(...page.aggs.filter((agg: Aggregate) => agg.path !== SearchPath.META_CDX_TYPE).map((agg: Aggregate) =>
                            {
                                let path = '';
                                if(!!agg.path) {
                                    path = agg.path;
                                } else {
                                    if(agg?.type == 'DELAY') {
                                        path = agg.delayPath;
                                    }
                                }
                                return path;
                            }
                        ));
                        if (!!this.wkfSupervisionFilterState && !!this.wkfSupervisionFilterState.datas && !!this.wkfSupervisionFilterState.datas.lastAgg) {
                            page.aggs = page.aggs.map((aggregate: Aggregate) => {
                                if (aggregate.path === this.wkfSupervisionFilterState.datas.lastAgg.path) {
                                    return this.wkfSupervisionFilterState.datas.lastAgg;
                                }
                                return aggregate;
                            });
                        }
                        this.wkfSupervisionSearchResultAction.updateTasksSucceeded(page as EsPage<CdxTask>);
                    }, (error: HttpErrorResponse) => {
                        this.wkfSupervisionSearchResultAction.updateTasksFailed(error);
                    }
                );
        } catch (error) {
            this.wkfSupervisionSearchResultAction.updateTasksFailed(error);
        }
    }

    // surcharge pour pouvoir récupérer les valeurs de tri par défaut de la config provenant de la bdd
    protected sortByKeyAndDirection(): OperatorFunction<HttpParams, HttpParams> {
        let res = super.sortByKeyAndDirection();

        if ( !this.sortKey && !this.sortDirection ) {
            // on va récupérer les valeurs par défaut provenant de la config des colonnes

            this.sortKey=this.currentSupervisionService.getCurrentTaskSortKey();
            this.sortDirection=this.currentSupervisionService.getCurrentTaskSortDirection();

            if ( !this.sortKey && !this.sortDirection ) {
                this.sortKey= TaskSearchResultService.TASK_PAGE_DEFAULT_SORT_KEY ;
                this.sortDirection= TaskSearchResultService.TASK_PAGE_DEFAULT_SORT_DIRECTION;
            }


            res= map((params) => {
                if (!!this.sortKey) {

                    let sort = '';
                    let order = AbstractSearchResultService.DESC;
                    if (!!this.sortDirection) {
                        order = this.sortDirection;
                    }
                    sort = this.sortKey + ':' + order;

                    params = params.append('_sort', sort);
                }

                return params;
            });
        }

        return res;
    }

   // surcharge pour gérer les cas spéciaux assignation et processState
    public filterItemsParams(path = null): OperatorFunction<HttpParams, HttpParams> {
        return pipe(
            // withLatestFrom(this.filterItems$),
            withLatestFrom(this.getFilterDatas$()),
            map(([httpParams, filterDatas]) => {
                if (!!filterDatas && !!filterDatas.filterItems && filterDatas.filterItems.length > 0) {
                    filterDatas.filterItems.forEach((filterItem: SearchItem) => {
                        if (!filterItem.operator) {
                            if (!path || (!!path && path !== filterItem.path)) {
                                httpParams = httpParams.append(filterItem.path, filterItem.value);
                            }
                        } else {
                            if (!path || (!!path && path !== filterItem.path)) {
                                httpParams = httpParams.append(filterItem.path + ':' + filterItem.operator, filterItem.value);
                            }
                        }
                    });

                }
                if( filterDatas.processState === ProcessStateEnum.ALL ) {
                    httpParams = httpParams.append('_state', ProcessStateEnum.ALL);
                } /*else  {
                    httpParams = httpParams.append('_state', ProcessStateEnum.OPEN);
                }*/

                return httpParams;
            })
        );
    }

    /*
    public filterItemsParams(path: any = null): OperatorFunction<HttpParams, HttpParams> {
        const currentUserId = this.authenticationService.getUserCredentials().id;
        return pipe(super.filterItemsParams(path),
            withLatestFrom(this.getFilterDatas$()),
            map( ([httpParams, filterDatas]) => {
                    if (!!filterDatas && !!filterDatas.filterItems && filterDatas.filterItems.length > 0) {
                        filterDatas.filterItems.forEach((filterItem: SearchItem) => {
                            if (!filterItem.operator) {
                                if (!path || (!!path && path !== filterItem.path)) {
                                    httpParams = httpParams.append(filterItem.path, filterItem.value);
                                }
                            } else {
                                if (!path || (!!path && path !== filterItem.path)) {
                                    httpParams = httpParams.append(filterItem.path + ':' + filterItem.operator, filterItem.value);
                                }
                            }
                        });
                    }

                    if( filterDatas.processState === ProcessStateEnum.ALL ) {
                        httpParams = httpParams.append('_state', ProcessStateEnum.ALL);
                    } else  {
                        httpParams = httpParams.append('_state', ProcessStateEnum.OPEN);
                    }

                    let newHttpParams = new HttpParams();
                    httpParams.keys().forEach(path => {

                        const values : string[] = httpParams.getAll(path);
                        values.forEach( (value) => {
                            if (path !== "cdx_wkf.assignation") {
                                newHttpParams = newHttpParams.append( path, value );
                            } else {
                                newHttpParams = newHttpParams.append(path + '.' + value, currentUserId);
                            }
                        });

                    })
                    return newHttpParams;
                }
            ));
    } */

    public exportExcel(): Observable<Blob> {
        return this.query()
            .pipe(
                this.workspaceParams(),
                this.searchItemsParams(),
                this.filterItemsParams(),
                this.sortByKeyAndDirection(),
                this.requestExport()
            );
    }

    protected requestExport(): OperatorFunction<HttpParams, Blob> {
        return switchMap(params => {
            if (AbstractSearchResultService.hasRequestItem(params) || this.getServiceType() === InternalRoutes.TASKS) {
                return this.httpClient.post(this.getExportExcelBaseUrl(), null, {
                    params: params,
                    responseType: 'blob'
                });
            } else {
                return of(new Blob());
            }
        });
    }

}
